import { useCallback, useMemo } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';

import { useMembershipBottomBannerData } from '../../../../../../context';

type Result = ModelHookResult<
	{
		buttonAmounts: ReadonlyArray<{ amount: number; amountLocalized: string; link: string }>;
		selectedAmount: { amount: number; amountLocalized: string };
		buttonText: string;
		otherLink: string;
		ctaButtonLink: string;
	},
	{
		onClick: () => void;
	}
>;

type Props = {
	slug: string;
	onClick: () => void;
};

export function useButtonContainer(props: Props): Result {
	const track = useTracking();
	const { translate, amountToBaseUnits } = useI18n();
	const {
		data: { currency, subscriptionConfig },
	} = useMembershipBottomBannerData();

	const encodedURI = useMemo(() => encodeURIComponent(props.slug), [props.slug]);

	const buttonAmounts = useMemo(() => {
		if (!subscriptionConfig?.amounts) return [];
		return subscriptionConfig.amounts.map(({ amount, amountLocalized }) => {
			return {
				amount,
				amountLocalized,
				link: `/p/${encodedURI}/psf/membership?source_location=petition_membership_popup&amount=${amountToBaseUnits(amount, currency)}`,
			};
		});
	}, [amountToBaseUnits, currency, encodedURI, subscriptionConfig]);

	const onClick = useCallback(() => {
		void track('petition_membership_popup_click');
		props.onClick();
	}, [track, props]);

	const selectedAmount = subscriptionConfig?.defaultAmount ?? { amount: 0, amountLocalized: '0' };
	return {
		data: {
			selectedAmount,
			buttonAmounts,
			buttonText: translate('fe.pages.sponsors.continue'),
			otherLink: `/p/${encodedURI}/psf/membership?source_location=petition_membership_popup&amount=0`,
			ctaButtonLink: `/p/${encodedURI}/psf/membership?source_location=petition_membership_popup&amount=${amountToBaseUnits(selectedAmount.amount, currency)}`,
		},
		actions: {
			onClick,
		},
	};
}
